import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { KeyCloakService } from '../core/services/keycloak.service';
import { UsersRestService } from './rest/users.rest.service';
import { INotifications } from '../interfaces/INotifications.interface';
import { IReturnValue } from '../core/interfaces/IReturnValue.interface';
import { IYesNoDialogData } from '../interfaces/IYesNoDialogData.interface';
import { DialogService } from './dialog.service';
import { firstValueFrom, from, Subscription, switchMap, timer } from 'rxjs';
import { ToastService } from './toast.service';
import { TranslateService } from '@ngx-translate/core';
import { INotificationFilter } from '../interfaces/INotificationFilter.interface';
import { ConfigService } from '../core/services/config.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  private readonly _keyCloakService: KeyCloakService = inject(KeyCloakService);
  private readonly _usersRestService: UsersRestService =
    inject(UsersRestService);
  private readonly _dialogService = inject(DialogService);
  private readonly _toastService = inject(ToastService);
  private readonly _translateService = inject(TranslateService);
  private readonly _config = inject(ConfigService).notificationsConfig;

  private readonly _allNotifications = signal<INotifications[] | undefined>(
    undefined,
  );

  private readonly unchangeablePrimariesNotificationsCount = signal<number>(0);

  public notifications = computed(() => {
    const n = this._allNotifications();
    return n?.sort((a, b) => {
      if (!a.read) return -1;
      if (!b.read) return 1;
      return new Date(b.read).getTime() - new Date(a.read).getTime();
    });
  });

  private noMoreLoading = signal<boolean>(false);
  public count = signal<number>(0);

  private filter = signal<INotificationFilter>({});

  private _polling$?: Subscription;

  public async initialize(): Promise<void> {
    this.filter.set({
      primary: true,
    });
    await this.getNotifications(this.filter());
    await this.getCount();
    await this.checkForPrimaryNotifications();
    this.startPolling();
  }

  private startPolling(): void {
    this._polling$ = timer(this._config.interval, this._config.interval)
      .pipe(switchMap(() => from(this.getCount())))
      .subscribe();
  }

  ngOnDestroy() {
    this._polling$?.unsubscribe();
  }

  public async getNotifications(filter?: INotificationFilter) {
    const r: IReturnValue = await this._usersRestService.getNotifications(
      this._keyCloakService.KtoId()!,
      filter,
    );
    const data = r.data as INotifications[];

    if (data.length <= 0) {
      this.noMoreLoading.set(true);
    }
    this._allNotifications.update((n) => {
      const map = new Map((n ?? []).map((notif) => [notif.id, notif]));

      data.forEach((newNotif) => {
        const existing = map.get(newNotif.id);
        // Only update if it doesn't exist or if existing is not primary
        if (!existing || !existing.primary) {
          map.set(newNotif.id, newNotif);
        }
      });

      return Array.from(map.values());
    });
  }

  private putNotification(id: number) {
    this._usersRestService
      .putNotifications(this._keyCloakService.KtoId()!, id)
      .subscribe({
        next: (data) => {
          const unchangeable = data.data as boolean;
          this.count.update((c) => c - 1);
          if (unchangeable) {
            this.unchangeablePrimariesNotificationsCount.update((c) => c + 1);
          }
          this.getCount().then();
        },
        error: () => {
          this._toastService.showError(
            this._translateService.instant('labels.error.unknown'),
          );
        },
      });
  }

  private async checkForPrimaryNotifications() {
    const primaryNotifications =
      this._allNotifications()?.filter((n) => {
        return n.primary && !n.read;
      }) ?? [];

    for (const notification of primaryNotifications) {
      await this.openNotifications(notification);
    }
  }

  private updateNotificationLocally(id: number): void {
    this._allNotifications.update((notifications) =>
      notifications?.map((n) => {
        const now = new Date().toISOString().split('T')[0];

        return n.id === id ? { ...n, read: now } : n;
      }),
    );
  }

  public async openNotifications(not: INotifications): Promise<void> {
    const dialogData: IYesNoDialogData = {
      yesButton: this._translateService.instant('buttons.close'),
      forceInteraction: true,
      appScrollNearEnd: true,
      headline: not.headline,
      content: not.body,
    };

    if (!not.read) {
      this.updateNotificationLocally(not.id);
      this.putNotification(not.id);
    }
    await firstValueFrom(this._dialogService.openYesNoDialog(dialogData));
  }

  private async getCount() {
    const r: IReturnValue =
      await this._usersRestService.getCountOfNotifications(
        this._keyCloakService.KtoId()!,
      );
    this.count.update((count) => {
      let newCount = r.data as number;
      newCount = newCount - this.unchangeablePrimariesNotificationsCount();

      if (newCount !== count) {
        this.filter.set({});
        this.noMoreLoading.set(false);
      }

      return newCount;
    });
  }

  public async loadNextPage() {
    if (this.noMoreLoading()) return;
    this.filter.update((f) => {
      return {
        page: (f.page ?? 1) + 1,
        pageSize: 10,
      };
    });
    await this.getNotifications(this.filter());
  }
}
